import { Autocomplete, Checkbox, TextField } from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTextDomainContext } from "@/app/providers";
import { isEqual } from "lodash";
import md5 from "md5";
import { AutocompleteMultiDropdownProps } from "./AutocompleteMultiDropdown.types";

export const AutocompleteMultiDropdown = (
  props: AutocompleteMultiDropdownProps,
) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const optionList = props.options;

  const optionAll = useMemo(() => {
    return [
      {
        value: "all",
        label: gettext("All"),
      },
    ];
  }, [gettext]);

  const componentLabel = useMemo(() => {
    return props.componentLabel || "";
  }, [props.componentLabel]);

  const showAll = useMemo(() => {
    return props.showAll || false;
  }, [props.showAll]);

  const options = useMemo(() => {
    return showAll ? [...optionAll, ...optionList] : optionList;
  }, [optionAll, optionList, showAll]);

  const selectSize = useMemo(() => {
    return props.size || "medium";
  }, [props.size]);

  const defaultValue = useMemo(() => {
    return props.defaultValue || [];
  }, [props.defaultValue]);

  let cacheKey = "AutocompleteMultiDropdown";
  let componentId = "autocomplete-multi-dropdown";
  if (props.componentLabel) {
    cacheKey = `AutocompleteDropdownMulti-${props.componentLabel}`;
    componentId = "autocomplete-dropdown-multi-" + md5(cacheKey);
  }

  const [newSelectedValue, setNewSelectedValue] = useState([]);

  const selectedValue = useMemo(() => {
    if (newSelectedValue.length) return newSelectedValue;

    if (defaultValue?.length) {
      const selected = options.filter((x) => defaultValue.includes(x.value));
      if (selected?.length) {
        return selected;
      }
    }

    if (
      componentLabel === "Subscription Plan" &&
      options.length &&
      props.required
    ) {
      return [options[0]];
    }

    return [];
  }, [componentLabel, defaultValue, newSelectedValue, options, props.required]);

  useEffect(() => {
    const values = selectedValue.map((v) => v.value);
    if (typeof props.onChange === "function") {
      if (!isEqual(defaultValue, values)) {
        props.onChange(selectedValue);
      }
    }
  }, [defaultValue, props, selectedValue]);

  useEffect(() => {
    const maxWidth = Math.max(...options.map((option) => option.label.length));
    const dynamicWidth = maxWidth * 5 + 100;
    const autocompleteElement = document.getElementById(componentId);
    if (autocompleteElement) {
      autocompleteElement.style.width = `${dynamicWidth}px`;
    }
  }, [componentId, options]);

  const handleChange = (event: any, value: any) => {
    localStorage.setItem(cacheKey, JSON.stringify(value));
    setNewSelectedValue(value);
    props.onChange(value);
  };

  return (
    <Autocomplete
      multiple
      id={componentId}
      size={selectSize}
      options={options}
      autoHighlight
      disableCloseOnSelect
      sx={{
        ...props.sx,
      }}
      value={selectedValue}
      onChange={handleChange}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      getOptionLabel={(option) => option.label || ""}
      renderOption={(propsOpt, option, { selected }) => {
        const { key, ...rest } = propsOpt;
        return (
          <li key={key} {...rest}>
            <Checkbox style={{ marginRight: 8 }} checked={selected} />
            {option.label}
          </li>
        );
      }}
      slotProps={{
        chip: {
          // optional custom props for Chips
          size: "small",
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          required={props.required}
          label={componentLabel}
          placeholder={componentLabel}
        />
      )}
    />
  );
};
